import type { PaymentMethodPayload } from 'braintree-web-drop-in';
import type {
  ISavedPaymentMethod,
  SavedPaymentMethodType,
} from '@/common/interfaces/payment.interface';

interface CreditCardDetails {
  cardholderName: string;
  cardType?: string;
  lastFour?: string;
  expirationMonth?: string;
  expirationYear?: string;
}

interface PayPalAccountDetails {
  email: string;
  firstName: string;
  lastName: string;
}

interface VenmoAccountDetails {
  username: string;
}

interface GooglePayDetails {
  cardType?: string;
  lastFour?: string;
  expirationMonth?: string;
  expirationYear?: string;
}

const normalize = (value?: string) => value?.trim().toLowerCase() ?? '';

const normalizeYear = (year?: string): number | null => {
  if (!year || !/^\d{2,4}$/.test(year.trim())) return null;
  const value = Number(year);
  return year.trim().length === 2 ? 2000 + value : value;
};

/**
 * Creates a display-level identity for one vaulted instrument.
 * Used by both HQ checkout surfaces to collapse duplicate vault entries without deleting them.
 */
export const getSavedPaymentMethodFingerprint = (paymentMethod: ISavedPaymentMethod): string => {
  if (
    (paymentMethod.type === 'credit_card' || paymentMethod.type === 'google_pay') &&
    paymentMethod.last4
  ) {
    return [
      paymentMethod.type,
      normalize(paymentMethod.cardType),
      paymentMethod.last4,
      paymentMethod.expirationMonth ?? '',
      paymentMethod.expirationYear ?? '',
    ].join(':');
  }

  if (paymentMethod.type === 'paypal' && paymentMethod.email) {
    return `paypal:${normalize(paymentMethod.email)}`;
  }

  if (paymentMethod.type === 'venmo' && paymentMethod.username) {
    return `venmo:${normalize(paymentMethod.username)}`;
  }

  return `token:${paymentMethod.token}`;
};

/**
 * Creates the fallback identity used when Vault Manager omits card expiry data.
 * It intentionally keeps Google Pay separate from a directly vaulted card.
 */
export const getSavedPaymentMethodLooseFingerprint = (
  paymentMethod: Pick<
    ISavedPaymentMethod,
    'type' | 'token' | 'last4' | 'cardType' | 'email' | 'username'
  >
): string => {
  if (
    (paymentMethod.type === 'credit_card' || paymentMethod.type === 'google_pay') &&
    paymentMethod.last4
  ) {
    return `${paymentMethod.type}:${normalize(paymentMethod.cardType)}:${paymentMethod.last4}`;
  }

  if (paymentMethod.type === 'paypal' && paymentMethod.email) {
    return `paypal:${normalize(paymentMethod.email)}`;
  }

  if (paymentMethod.type === 'venmo' && paymentMethod.username) {
    return `venmo:${normalize(paymentMethod.username)}`;
  }

  return `token:${paymentMethod.token}`;
};

/**
 * Returns true only after the final day of a saved card's expiration month.
 * Missing expiry is left for Braintree to validate because wallet methods may not expose it.
 */
export const isSavedPaymentMethodExpired = (
  paymentMethod: ISavedPaymentMethod,
  now = new Date()
): boolean => {
  if (paymentMethod.type !== 'credit_card') return false;

  const month = Number(paymentMethod.expirationMonth);
  const year = normalizeYear(paymentMethod.expirationYear);
  if (!Number.isInteger(month) || month < 1 || month > 12 || year === null) return false;

  return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
};

const isDisplayableSavedPaymentMethod = (paymentMethod: ISavedPaymentMethod): boolean => {
  if (!paymentMethod.token || paymentMethod.type === 'unknown') return false;
  if (paymentMethod.type === 'credit_card' || paymentMethod.type === 'google_pay') {
    return Boolean(paymentMethod.last4);
  }
  if (paymentMethod.type === 'paypal') return Boolean(paymentMethod.email?.trim());
  if (paymentMethod.type === 'venmo') return Boolean(paymentMethod.username?.trim());
  return false;
};

/**
 * Removes unusable, expired, and duplicate methods before checkout renders them.
 * The default entry wins a duplicate collision; otherwise the API's first entry is retained.
 */
export const cleanSavedPaymentMethods = (
  paymentMethods: ISavedPaymentMethod[],
  now = new Date()
): ISavedPaymentMethod[] => {
  const byFingerprint = new Map<string, ISavedPaymentMethod>();

  paymentMethods.forEach((paymentMethod) => {
    if (!isDisplayableSavedPaymentMethod(paymentMethod)) return;
    if (isSavedPaymentMethodExpired(paymentMethod, now)) return;

    const fingerprint = getSavedPaymentMethodFingerprint(paymentMethod);
    const current = byFingerprint.get(fingerprint);
    if (!current || (paymentMethod.isDefault && !current.isDefault)) {
      byFingerprint.set(fingerprint, paymentMethod);
    }
  });

  return Array.from(byFingerprint.values()).sort(
    (first, second) => Number(second.isDefault) - Number(first.isDefault)
  );
};

export const getDefaultSavedPaymentMethod = (
  paymentMethods: ISavedPaymentMethod[]
): ISavedPaymentMethod | null =>
  paymentMethods.find((paymentMethod) => paymentMethod.isDefault) ?? paymentMethods[0] ?? null;

export const formatSavedPaymentMethodLabel = (paymentMethod: ISavedPaymentMethod): string => {
  if (paymentMethod.type === 'google_pay') {
    return paymentMethod.cardType
      ? `Google Pay · ${paymentMethod.cardType} •••• ${paymentMethod.last4}`
      : `Google Pay •••• ${paymentMethod.last4}`;
  }
  if (paymentMethod.type === 'credit_card') {
    return `${paymentMethod.cardType || 'Card'} •••• ${paymentMethod.last4}`;
  }
  if (paymentMethod.type === 'paypal') return `PayPal ${paymentMethod.email}`;
  if (paymentMethod.type === 'venmo') return `Venmo ${paymentMethod.username}`;
  return 'Saved payment method';
};

/**
 * Maps a selected saved method to the existing order API's payment metadata shape.
 * It carries display-safe values only; the single-use nonce is submitted separately.
 */
export const getSavedPaymentMethodDetails = (paymentMethod: ISavedPaymentMethod) => {
  if (paymentMethod.type === 'credit_card') {
    return {
      cardholderName: '',
      paymentMethod: 'credit_card',
      paymentMethodValue: 1,
      vaulted: true,
    };
  }
  if (paymentMethod.type === 'paypal') {
    return {
      paypalEmail: paymentMethod.email ?? '',
      paypalPayerName: '',
      paymentMethod: 'paypal_account',
      paymentMethodValue: 8,
      vaulted: true,
    };
  }
  if (paymentMethod.type === 'venmo') {
    return {
      venmoUsername: paymentMethod.username ?? '',
      paymentMethod: 'venmo_account',
      paymentMethodValue: 3,
      vaulted: true,
    };
  }
  if (paymentMethod.type === 'google_pay') {
    return {
      paymentMethod: 'android_pay_card',
      paymentMethodValue: 7,
      vaulted: true,
    };
  }
  return {};
};

export const getPaymentMethodPayloadType = (
  type: PaymentMethodPayload['type']
): SavedPaymentMethodType => {
  if (type === 'CreditCard') return 'credit_card';
  if (type === 'PayPalAccount') return 'paypal';
  if (type === 'VenmoAccount') return 'venmo';
  if (type === 'AndroidPayCard') return 'google_pay';
  return 'unknown';
};

/**
 * Prevents a new Drop-in method from being vaulted when the same instrument is already saved.
 */
export const isPaymentMethodPayloadAlreadySaved = (
  payload: PaymentMethodPayload,
  savedPaymentMethods: ISavedPaymentMethod[]
): boolean => {
  const type = getPaymentMethodPayloadType(payload.type);
  const details = payload.details as CreditCardDetails &
    PayPalAccountDetails &
    VenmoAccountDetails &
    GooglePayDetails;
  const candidate: ISavedPaymentMethod = {
    type,
    token: '',
    last4: details.lastFour,
    cardType: details.cardType,
    expirationMonth: details.expirationMonth,
    expirationYear: details.expirationYear,
    email: details.email,
    username: details.username,
    isDefault: false,
  };
  const candidateLooseFingerprint = getSavedPaymentMethodLooseFingerprint(candidate);

  return savedPaymentMethods.some(
    (savedPaymentMethod) =>
      getSavedPaymentMethodLooseFingerprint(savedPaymentMethod) === candidateLooseFingerprint
  );
};

/** Distinguishes unusable vault entries from ordinary declines such as insufficient funds. */
export const isSavedPaymentMethodUnavailableError = (message: string): boolean =>
  /(?:payment method|token).*(?:not found|invalid|expired)|expired card/i.test(message);
/*
 * other                    : 0
 * credit card              : 1
 * cash/check               : 2
 * venmo                    : 3
 * Refund for participant   : 4
 * zelle                    : 5
 * wire_transfer            : 6
 * Google Pay               : 7
 * Paypal                   : 8
 * Apple Pay                : 9
 */

export const getPaymentMethodDetails = (
  selectedPayment: PaymentMethodPayload,
  isConsentGiven = false
) => {
  const methodType = selectedPayment.type;
  const { details } = selectedPayment;

  if (methodType === 'CreditCard') {
    return {
      cardholderName: (details as CreditCardDetails).cardholderName,
      paymentMethod: 'credit_card',
      vaulted: selectedPayment.vaulted ?? false,
      paymentMethodValue: 1,
    };
  }

  if (methodType === 'PayPalAccount') {
    return {
      paypalEmail: (details as PayPalAccountDetails).email,
      paypalPayerName: `${(details as PayPalAccountDetails).firstName} ${
        (details as PayPalAccountDetails).lastName
      }`,
      paymentMethod: 'paypal_account',
      vaulted: isConsentGiven,
      paymentMethodValue: 8,
    };
  }

  if (methodType === 'VenmoAccount') {
    return {
      venmoUsername: (details as VenmoAccountDetails).username,
      paymentMethod: 'venmo_account',
      vaulted: isConsentGiven,
      paymentMethodValue: 3,
    };
  }

  if (methodType === 'AndroidPayCard') {
    return {
      paymentMethod: 'android_pay_card',
      vaulted: isConsentGiven,
      paymentMethodValue: 7,
    };
  }

  return {};
};

/**
 * Checkout-specific payment metadata: affirmative consent is the only signal that a new
 * card or wallet may be stored. Invoice payment keeps using getPaymentMethodDetails unchanged.
 */
export const getCheckoutPaymentMethodDetails = (
  selectedPayment: PaymentMethodPayload,
  isConsentGiven = false
) => {
  const paymentMethodDetails = getPaymentMethodDetails(selectedPayment, isConsentGiven);
  if (!('paymentMethod' in paymentMethodDetails)) return paymentMethodDetails;

  return {
    ...paymentMethodDetails,
    vaulted: isConsentGiven,
  };
};

/**
 * Split payment API error message into primary and secondary text using newline.
 *
 * @param message - payment API error message
 */
export const splitPaymentApiErrorMessage = (message: string) => {
  const [primaryText = '', ...secondaryParts] = message.split(/\r?\n/);
  const secondarytText = secondaryParts.join(' ').trim();

  return {
    primaryText: primaryText.trim(),
    secondarytText,
  };
};
